/**
 * Pure state machine for `defineScopedOperation`.
 *
 * The Vue composable owns refs, watchers, async I/O, and the shared
 * tracking/pending store. This reducer owns the *transitions* — everything
 * that decides "given the current state and this event, what is the next
 * state and what should happen as a side effect?"
 *
 * Keeping it pure means the transitions are unit-testable without mounting
 * Vue or stubbing the realtime context.
 */

export interface ScopedOperationState<R> {
  data: R | undefined
  error: Error | null
  isResolving: boolean
  terminalHandled: boolean
  idempotencyKey: string | null
  startedAt: Date | null
}

export function initialScopedOperationState<R>(): ScopedOperationState<R> {
  return {
    data: undefined,
    error: null,
    isResolving: false,
    terminalHandled: false,
    idempotencyKey: null,
    startedAt: null,
  }
}

export type ScopedOperationAction<R> =
  | { type: 'BEGIN_MUTATION', idempotencyKey: string, startedAt: Date }
  | { type: 'ENQUEUE_FAILED', error: Error }
  | { type: 'TIMEOUT_WAITING_FOR_RUN', error: Error }
  | { type: 'TOKEN_UNAVAILABLE', error: Error }
  | { type: 'TERMINAL_NON_COMPLETED', error: Error }
  | { type: 'PARSE_STARTED' }
  | { type: 'PARSE_SUCCEEDED', data: R }
  | { type: 'PARSE_FAILED', error: Error }
  | {
    type: 'MATCHED_EXISTING_OPERATION'
    operationId: string
    startedAt: Date
  }
  | { type: 'RESET' }

/**
 * Side effects the composable must apply after a transition. The reducer
 * stays pure; the composable translates these into Vue/tanstack/store calls.
 *
 * Effects are emitted in the order they should run.
 */
export type ScopedOperationEffect<R> =
  | { type: 'NOTIFY_RESOLVED', data: R }
  | { type: 'NOTIFY_ERROR', error: Error }
  | { type: 'RESOLVE_ASYNC', data: R }
  | { type: 'REJECT_ASYNC', error: Error }
  | { type: 'STOP_ACTIVE' }
  | { type: 'START_ACTIVE' }
  | { type: 'SET_TRACKING', operationId: string }

export interface ScopedOperationTransition<R> {
  state: ScopedOperationState<R>
  effects: ScopedOperationEffect<R>[]
}

function failure<R>(
  state: ScopedOperationState<R>,
  error: Error,
  options: { terminalHandled: boolean },
): ScopedOperationTransition<R> {
  return {
    state: {
      ...state,
      error,
      isResolving: false,
      terminalHandled: options.terminalHandled,
      idempotencyKey: null,
      startedAt: null,
    },
    effects: [
      { type: 'NOTIFY_ERROR', error },
      { type: 'REJECT_ASYNC', error },
      { type: 'STOP_ACTIVE' },
    ],
  }
}

export function scopedOperationReducer<R>(
  state: ScopedOperationState<R>,
  action: ScopedOperationAction<R>,
): ScopedOperationTransition<R> {
  switch (action.type) {
    case 'BEGIN_MUTATION':
      return {
        state: {
          ...state,
          data: undefined,
          error: null,
          terminalHandled: false,
          idempotencyKey: state.idempotencyKey ?? action.idempotencyKey,
          startedAt: action.startedAt,
        },
        effects: [{ type: 'START_ACTIVE' }],
      }

    case 'MATCHED_EXISTING_OPERATION':
      return {
        state: {
          ...state,
          data: undefined,
          error: null,
          terminalHandled: false,
          startedAt: action.startedAt,
        },
        effects: [
          { type: 'SET_TRACKING', operationId: action.operationId },
          { type: 'START_ACTIVE' },
        ],
      }

    case 'ENQUEUE_FAILED':
    case 'TIMEOUT_WAITING_FOR_RUN':
    case 'TOKEN_UNAVAILABLE':
      return failure(state, action.error, { terminalHandled: false })

    case 'TERMINAL_NON_COMPLETED':
      return failure(state, action.error, { terminalHandled: true })

    case 'PARSE_STARTED':
      return {
        state: { ...state, isResolving: true, terminalHandled: true },
        effects: [],
      }

    case 'PARSE_SUCCEEDED':
      return {
        state: {
          ...state,
          data: action.data,
          error: null,
          isResolving: false,
          idempotencyKey: null,
        },
        effects: [
          { type: 'NOTIFY_RESOLVED', data: action.data },
          { type: 'RESOLVE_ASYNC', data: action.data },
          { type: 'STOP_ACTIVE' },
        ],
      }

    case 'PARSE_FAILED':
      return failure(state, action.error, { terminalHandled: true })

    case 'RESET':
      return {
        state: initialScopedOperationState<R>(),
        effects: [
          { type: 'REJECT_ASYNC', error: new Error('Reset') },
          { type: 'STOP_ACTIVE' },
        ],
      }
  }
}
